import {
  AT,
  BE,
  CH,
  CZ,
  DE,
  DK,
  ES,
  FR,
  type FlagComponent,
  GB,
  HU,
  IT,
  LU,
  MC,
  NL,
  PL,
  PT,
  RO,
  SE,
  SI,
  SK,
} from "country-flag-icons/react/3x2";

import { objectKeys } from "@/common/utils/object.utils";

/**
 * These are the countries which are supported in the website. Please use this
 * instead of keys of {@link COUNTRY_MAP} to provide better readability.
 */
export const SUPPORTED_COUNTRIES = [
  // Full Support
  "NL",
  "BE",
  "DE",
  "FR",
  "ES",
  "IT",
  // Partial Support
  "AT",
  "CZ",
  "DK",
  "HU",
  "LU",
  "MC",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
  "CH",
  "GB",
] as const;

export const COUNTRY_CODES = SUPPORTED_COUNTRIES.map(key =>
  key.toLocaleLowerCase()
);

export type COUNTRY_ID = (typeof SUPPORTED_COUNTRIES)[number];

const withSize = (flag: FlagComponent) => {
  // Set default width and height to 16x12 to match the default size of the
  // country-flag-icons package.
  // @ts-expect-error - We are setting the default props here. Not sure how to
  // make typescript understand that.
  flag.defaultProps = { width: 16, height: 12 };
  return flag;
};

/**
 *  This is the map of all country related data. This map can contain data for
 *  more info than {@link SUPPORTED_COUNTRIES}.
 */
export const COUNTRY_MAP: Record<
  string,
  {
    flag: FlagComponent;
    name?: string;
  }
> = {
  NL: { flag: withSize(NL), name: "common_netherlands" },
  BE: { flag: withSize(BE), name: "common_belgium" },
  DE: { flag: withSize(DE), name: "common_germany" },
  FR: { flag: withSize(FR), name: "common_france" },
  AT: { flag: withSize(AT), name: "common_austria" },
  ES: { flag: withSize(ES), name: "common_spain" },
  HU: { flag: withSize(HU) },
  IT: { flag: withSize(IT), name: "common_italy" },
  LU: { flag: withSize(LU), name: "common_luxembourg" },
  CZ: { flag: withSize(CZ) },
  DK: { flag: withSize(DK) },
  MC: { flag: withSize(MC) },
  PL: { flag: withSize(PL) },
  PT: { flag: withSize(PT) },
  RO: { flag: withSize(RO) },
  SE: { flag: withSize(SE) },
  SI: { flag: withSize(SI) },
  SK: { flag: withSize(SK) },
  CH: { flag: withSize(CH), name: "common_switzerland" },
  GB: { flag: withSize(GB), name: "common_united_kingdom" },
} satisfies Record<
  COUNTRY_ID,
  {
    flag: FlagComponent;
    name?: string;
  }
>;

export const COUNTRY_MAP_KEYS = objectKeys(COUNTRY_MAP);

/**
 * ISO 3166-1 alpha-2 codes of the EU customs territory: the 27 member states
 * plus Monaco, which sits inside it via France. A parcel moving between two of
 * these crosses no customs border.
 *
 * Mirrors the same list in the gateway (`sendcloud-customs.static.ts`), which
 * decides whether a shipping label needs a customs declaration. The two must
 * agree: if this list says a parcel clears customs and the gateway's says it
 * does not, the buyer is told the wrong thing about who pays.
 */
const EU_CUSTOMS_TERRITORY = new Set<string>([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
  "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "MC",
  "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
]);

/**
 * Whether a parcel from `originCountry` to `destinationCountry` crosses a
 * customs border, meaning the buyer can be charged import VAT, duty and a
 * carrier clearance fee on delivery.
 *
 * Deliberately derived from the two countries rather than a list of
 * destinations: a UK seller shipping to a UK buyer crosses nothing, while a
 * Dutch seller shipping to Norway crosses a border even though Norway is on
 * nobody's list. Returns false when either country is unknown — we would rather
 * omit the notice than show it on a domestic order.
 */
export const crossesCustomsBorder = (
  originCountry?: string | null,
  destinationCountry?: string | null
): boolean => {
  if (!originCountry || !destinationCountry) {
    return false;
  }
  const from = originCountry.toUpperCase();
  const to = destinationCountry.toUpperCase();
  if (from === to) {
    return false;
  }
  return !(EU_CUSTOMS_TERRITORY.has(from) && EU_CUSTOMS_TERRITORY.has(to));
};
